import React from 'react';
import { withRouter } from 'react-router';
import { useSelector } from 'react-redux';

import get from 'lodash/get';
import noop from 'lodash/noop';

import Box from 'Common/Box/Box';
import DownloadEntities from '../download-entities/DownloadEntities';
import EntityViewer from '../entity-viewer/EntityViewer';
import ShareableLinks from '../shareable-links/ShareableLinks';
import { DatasetInspectorContainer } from '../dataset-inspector/DatasetInspectorContainer';

import PipeOutputSection from './pipe-output-section/PipeOutputSection';

import type { RouteComponentProps, RouteComponent } from 'react-router';
import type { RootState } from '../../types/state.types';
import type { Pipe, PipeConfigType } from '../../types/pipes.types';

import './PipeOutputStyle.css';

const PipeOutput: React.FC<
  RouteComponentProps<{}, any> & {
    inputPipe?: Pipe;
    subId?: string;
    addCustomActions: () => void;
    removeCustomActions: () => void;
    registerRefresh: () => void;
    unregisterRefresh: () => void;
  }
> = ({
  params,
  subId,
  inputPipe,
  addCustomActions = noop,
  removeCustomActions = noop,
  registerRefresh = noop,
  unregisterRefresh = noop,
}) => {
  const { pipeID } = params;

  const subscriptionId = params?.subId ?? subId;
  const currentPipeId = pipeID ?? inputPipe?._id;

  const pipe = useSelector((state: RootState) => inputPipe ?? state.pipes[currentPipeId]);
  const subUrl = useSelector((state: RootState) => state.subscription.url);
  const token = useSelector((state: RootState) => state.subscription.token);
  const darkModeActive = useSelector((state: RootState) => state.theme.dark);

  const sinkType = pipe?.config?.effective?.sink?.type;

  if (
    sinkType === 'dataset' ||
    sinkType === 'replicated_dataset' ||
    sinkType === 'mirrored_dataset'
  ) {
    const datasetID = get(pipe, 'config.effective.sink.dataset');

    if (datasetID) {
      return (
        <DatasetInspectorContainer
          datasetId={datasetID}
          registerRefresh={registerRefresh}
          unregisterRefresh={unregisterRefresh}
          addCustomActions={addCustomActions}
          removeCustomActions={removeCustomActions}
          tab="output"
        />
      );
    }
  }

  let downloadEntities = null;
  let shareableLinks = null;
  let entityViewer = null;

  const sinkIsEndpointTypes: Partial<PipeConfigType>[] = [
    'csv_endpoint',
    'http_endpoint',
    'xml_endpoint',
    'excel_endpoint',
  ];

  const sinkIsEndpoint = sinkIsEndpointTypes.includes(sinkType);

  const DownloadEntitiesComponent = DownloadEntities as any;

  if (sinkIsEndpoint) {
    let type = sinkType.split('_')[0];

    if (type === 'http') {
      type = 'entities';
    }

    downloadEntities = (
      <PipeOutputSection className="pipe-output__download-entities">
        <PipeOutputSection.Header className={darkModeActive && 'white-color'}>
          Download entities
        </PipeOutputSection.Header>
        {/* @ts-ignore */}
        <PipeOutputSection.Description>
          <p>
            {`This pipe pushes its entities to the `}
            <code>{`/publisher/${type}`}</code>
            {` endpoint. You can use the download form below to download entities to your computer.`}
          </p>
        </PipeOutputSection.Description>
        {/* @ts-ignore */}
        <PipeOutputSection.Content>
          {/* @ts-ignore */}
          <Box>
            <Box.Header>
              <Box.Title>Download entities</Box.Title>
            </Box.Header>
            {/* @ts-ignore */}
            <Box.Content padding>
              <DownloadEntitiesComponent
                url={`${subUrl}/publishers/${encodeURIComponent(pipe._id)}/${type}`}
                token={token}
                type={type === 'entities' ? 'json' : type}
                fileName={encodeURIComponent(pipe._id)}
                pipeName={encodeURIComponent(pipe._id)}
                subId={subscriptionId}
              />
            </Box.Content>
          </Box>
        </PipeOutputSection.Content>
      </PipeOutputSection>
    );

    shareableLinks = (
      <PipeOutputSection className="pipe-output__shareable-links">
        <PipeOutputSection.Header className={darkModeActive && 'white-color'}>
          Shareable links
        </PipeOutputSection.Header>
        {/* @ts-ignore */}
        <PipeOutputSection.Description>
          <p>
            You can give read-only access to this endpoint by generating a url with an access token.
            The access token will work until it expires or is deleted.
          </p>
        </PipeOutputSection.Description>
        {/* @ts-ignore */}
        <PipeOutputSection.Content>
          <ShareableLinks pipeId={get(pipe, '_id')} subUrl={subUrl} token={token} />
        </PipeOutputSection.Content>
      </PipeOutputSection>
    );
  }

  if (!sinkIsEndpoint) {
    entityViewer = (
      <PipeOutputSection className="pipe-output__entites">
        <PipeOutputSection.Header className={darkModeActive && 'white-color'}>
          Sink entities
        </PipeOutputSection.Header>
        {/* @ts-ignore */}
        <PipeOutputSection.Description>
          <p>This is how the entities look as they enter the sink.</p>
        </PipeOutputSection.Description>
        <PipeOutputSection.Content className="pipe-output__entity-viewer">
          {pipe && <EntityViewer pipe={pipe} stage="sink" />}
        </PipeOutputSection.Content>
      </PipeOutputSection>
    );
  }

  return (
    <div className="pipe-output">
      {downloadEntities}
      {shareableLinks}
      {entityViewer}
    </div>
  );
};

const Routed = withRouter(PipeOutput as unknown as RouteComponent);

export { Routed as PipeOutput };
